import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Image,
  Link as ChakraLink,
  Skeleton,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  MdAssignment,
  MdCalendarMonth,
  MdDescription,
  MdHomeWork,
  MdMenuBook,
  MdPendingActions,
  MdPlayCircle,
  MdQuiz,
  MdVideoLibrary,
} from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import StudentPortalShell from "../../../components/student/StudentPortalShell";
import { studentPalette, studentInk } from "../../../constants/studentPalette";
import { useAdminTheme } from "../../../context/AdminThemeContext";
import {
  studentOutlineButtonDarkSx,
  studentSurfaces,
} from "../../../constants/studentThemeSurfaces";
import CourseVideoModal from "../../../components/student/CourseVideoModal";
import PdfPreviewModal, {
  examSubmissionPdfUrl,
  homeworkSubmissionPdfUrl,
  lessonPdfPublicUrl,
} from "../../../components/student/PdfPreviewModal";
import AddVoucher from "../../../containers/student/models/add-voucher";
import SolveHomework from "../../../containers/student/models/solve-homework";
import { countSeens } from "../../../containers/student/video/func";
import { getChapterContentRequest } from "../../../modules/students-modules/chapters/Actions";
import { openAddVoucherModel, openSolveHomeworkModel } from "../../../modules/admin-modules/models/Actions";
import { getAllSectionsRequest, getSectionRequest } from "../../../modules/students-modules/sections/Actions";
import { examRedoRequest } from "../../../modules/students-modules/exams/Actions";
import {
  moreVideoViewsRequest,
} from "../../../modules/students-modules/videos/Actions";
import { useStartsInLabel, useCountdownNow, useStudentExamDeadline } from "../../../hooks/useStartsInLabel";
import { formatApiDateTimeLocal, formatStudentExamDeadlineLine } from "../../../utils/examSchedule";
import { LiveSessionJoinControls } from "../../../components/student/LiveSessionsPanel";
import {
  getLiveSessionPhase,
} from "../../../utils/liveSessionStatus";

const C = {
  ...studentPalette,
  border: studentSurfaces.border,
  paper: studentSurfaces.raised,
  sand: studentSurfaces.canvas,
  muted: studentSurfaces.textMuted,
  tertiary: studentInk,
};

function publicAssetUrl(path) {
  if (!path) return null;
  if (String(path).startsWith("http")) return path;
  const raw = process.env.REACT_APP_API_URL || "https://api.edutopiaonline.com/";
  const base = String(raw).replace(/\s/g, "").replace(/['"]/g, "").replace(/\/$/, "");
  const clean = String(path).replace(/^\//, "");
  return `${base}/public/${clean}`;
}

function itemTitle(item) {
  return item?.title || item?.name || "Untitled";
}

function videoThumbnailUrl(thumbnail) {
  if (thumbnail == null) return null;
  const value = String(thumbnail).trim();
  if (!value || value.toLowerCase() === "null" || value.toLowerCase() === "undefined") return null;
  return `https://api.edutopiaonline.com/public/thumbnails/${value}`;
}

function questionCodeFromTitle(title) {
  const match = String(title || "").trim().match(/^Q\s*(\d+)\b/i);
  if (!match) return null;
  return `Q${match[1]}`;
}

function QuestionCodePlaceholder({ label }) {
  const wide = label.length > 2;
  return (
    <Flex position="absolute" inset={0} bg="#FBF6F1" align="center" justify="center" pt={3}>
      <Flex direction="column" align="center">
        <Text
          fontFamily="'Playfair Display', Georgia, 'Times New Roman', serif"
          fontWeight="500"
          fontSize={wide ? "3.15rem" : "4.15rem"}
          lineHeight="0.85"
          color="#6B1F2C"
          letterSpacing="-0.03em"
        >
          {label}
        </Text>
        <Box mt="0.55rem" h="2px" w={wide ? "7.25rem" : "6.4rem"} bg="#6B1F2C" />
      </Flex>
    </Flex>
  );
}

function videoAlertMessage(v) {
  const vc = v?.views_count?.views_count;
  if (vc === 3) return "Please contact technical support";
  if (vc === 4) {
    return "You have exceeded the allowed watch duration. You can request a voucher and contact your assistant.";
  }
  if (v?.suspended === 1 && v?.user_video_suspended !== 0) {
    if (v?.related_to_homework === 1 && v?.exams?.length) {
      return `Video locked — submit homework or complete the exam ${v.exams[0]?.name}`;
    }
    if (v?.related_to_homework === 1 && !v?.exams?.length) {
      return "Video locked — please submit homework";
    }
    if (v?.related_to_homework === 0 && v?.exams?.length) {
      return `Video locked — please complete the exam ${v.exams[0]?.name}`;
    }
    return "Video locked — request a voucher and contact your assistant";
  }
  return "";
}

function needsVoucherActions(v) {
  const used = v?.views_count?.views_count ?? 0;
  const max = v?.max_allowed_views ?? 0;
  const overOrEqual = max > 0 && max <= used;
  const locked = v?.suspended == 1 && v?.user_video_suspended != 0;
  return overOrEqual || locked;
}

function examTypeLabel(item) {
  const t = Number(item?.type);
  if (t === 2) return "Homework/Classified";
  if (t === 0) return "Quiz";
  return "Exam";
}

function formatMetaLine(item, kind) {
  if (kind === "video") {
    const dur = item?.duration || item?.video_duration || item?.length;
    const d = dur != null ? `${dur}${String(dur).includes(":") ? "" : " min"}` : "";
    return d ? `Video session • ${d}` : "Video session";
  }
  if (kind === "lesson") {
    const sz = item?.size || item?.file_size;
    return sz ? `PDF • ${sz}` : "PDF document";
  }
  if (kind === "exam") {
    const n =
      item?.total_count ??
      item?.questions_count ??
      item?.questions ??
      item?.question_count ??
      item?.no_of_questions;
    const timed = item?.timer == 1 && item?.time != null && String(item.time).trim() !== "";
    const pts = item?.score;
    const hasPts = pts != null && String(pts).trim() !== "" && Number(pts) > 0;
    const parts = [];
    if (n != null && String(n).trim() !== "") parts.push(`${n} questions`);
    if (timed) parts.push(`${item.time} min`);
    if (hasPts) parts.push(`${pts} pts`);
    return parts.length ? `${examTypeLabel(item)} • ${parts.join(" • ")}` : examTypeLabel(item);
  }
  if (kind === "live") {
    const localStart = formatApiDateTimeLocal(item?.start_time);
    if (localStart) return `Live session · ${localStart}`;
    return item?.subtitle || item?.description || "Live session";
  }
  if (kind === "homework") {
    const d = item?.created_at;
    return d ? `Homework • ${dayjs(d).format("D MMM YYYY")}` : "Homework";
  }
  return "";
}

function homeworkActionLabel(hw) {
  const sh = hw?.student_homework;
  const hasFile = sh?.file;
  const status = sh?.status;
  if ((status === 0 || status === 1) && hasFile) return "Preview";
  if (status === 2) return "Resubmit";
  return "Solve";
}

function isExamCorrected(ex) {
  return Number(ex?.correction) === 1;
}

function isHomeworkCorrected(hw) {
  return Number(hw?.student_homework?.correction ?? hw?.correction) === 1;
}

function homeworkStatusSummary(hw) {
  const sh = hw?.student_homework;
  if (!sh) return null;
  if (sh.status === 0) return "Pending review";
  if (sh.status === 1) return "Accepted";
  if (sh.status === 2) return "Refused — you can resubmit";
  return "Submitted";
}

function HomeworkCurriculumCard({ hw, disabled, onPrimary, onPreview }) {
  const studentGroupId = useSelector((s) => s.user.userData?.group?.id);
  const deadline = useStudentExamDeadline(hw, studentGroupId, 1000);
  const deadlineLine = formatStudentExamDeadlineLine(deadline);
  const closed = deadline.closed;
  const statusLine = homeworkStatusSummary(hw);
  const sh = hw?.student_homework;
  const label = homeworkActionLabel(hw);
  const pdfUnlocked = isHomeworkCorrected(hw);
  const primaryOpensPdf = (sh?.status === 0 || sh?.status === 1) && Boolean(sh?.file);
  const submissionClosed = closed && (label === "Solve" || label === "Resubmit");
  const primaryDisabled = disabled || submissionClosed || (primaryOpensPdf && !pdfUnlocked);

  return (
    <CurriculumCard>
      <CurriculumCardMedia
        badge="Homework"
        badgeTone="homework"
        hero="linear(to-br, rgba(168,160,150,0.2), rgba(91, 13, 22,0.08))"
      >
        <MdHomeWork color={C.primary} size={48} />
      </CurriculumCardMedia>
      <CurriculumCardBody>
        <Text fontSize="sm" fontWeight="800" color={C.primary} noOfLines={2} mb={1}>
          {itemTitle(hw)}
        </Text>
        <Text
          fontSize="10px"
          fontWeight="700"
          color={`${C.primary}99`}
          textTransform="uppercase"
          letterSpacing="0.06em"
          mb={statusLine || deadlineLine || closed || sh?.evaluation || sh?.comment ? 2 : 3}
        >
          {formatMetaLine(hw, "homework")}
        </Text>
        {closed && submissionClosed ? (
          <Text fontSize="xs" fontWeight="800" color={C.primary} mb={1}>
            Ended
          </Text>
        ) : deadlineLine ? (
          <Text fontSize="xs" fontWeight="800" color={C.primary} mb={1}>
            {deadlineLine}
          </Text>
        ) : null}
        {statusLine ? (
          <Text fontSize="xs" fontWeight="700" color={C.muted} mb={1}>
            {statusLine}
          </Text>
        ) : null}
        {sh?.evaluation ? (
          <Text fontSize="11px" color={C.muted} noOfLines={3} mb={1}>
            Evaluation: {sh.evaluation}
          </Text>
        ) : null}
        {sh?.comment ? (
          <Text fontSize="11px" color={C.muted} noOfLines={3} mb={3}>
            Comment: {sh.comment}
          </Text>
        ) : statusLine || deadlineLine ? (
          <Box mb={3} />
        ) : null}
        <Box mt="auto">
          <VStack spacing={2} w="full" align="stretch">
            <Button
              w="full"
              size="sm"
              bg={C.secondary}
              color="white"
              fontWeight="800"
              textTransform="uppercase"
              fontSize="10px"
              borderRadius="lg"
              isDisabled={primaryDisabled}
              cursor={primaryDisabled ? "not-allowed" : "pointer"}
              _hover={primaryDisabled ? {} : { bg: C.primary }}
              _disabled={{ opacity: 0.55, cursor: "not-allowed" }}
              onClick={onPrimary}
            >
              {submissionClosed ? "Ended" : label}
            </Button>
            {sh?.status === 2 && sh?.file ? (
              <Button
                w="full"
                size="sm"
                variant="outline"
                borderWidth="2px"
                borderColor={C.primary}
                color={C.primary}
                fontWeight="800"
                textTransform="uppercase"
                fontSize="10px"
                borderRadius="lg"
                isDisabled={disabled || !pdfUnlocked}
                cursor={disabled || !pdfUnlocked ? "not-allowed" : "pointer"}
                _hover={disabled || !pdfUnlocked ? {} : { bg: "rgba(91, 13, 22, 0.06)" }}
                _disabled={{ opacity: 0.55, cursor: "not-allowed" }}
                onClick={onPreview}
              >
                Preview PDF
              </Button>
            ) : null}
          </VStack>
        </Box>
      </CurriculumCardBody>
    </CurriculumCard>
  );
}

function ExamCurriculumCard({
  ex,
  courseReturnPath,
  redoLoadingExamId,
  onExamPreview,
  onRequestRedo,
}) {
  const studentGroupId = useSelector((s) => s.user.userData?.group?.id);
  const startsIn = useStartsInLabel(ex?.start_time, 1000);
  const deadline = useStudentExamDeadline(ex, studentGroupId, 1000);
  const deadlineLine = formatStudentExamDeadlineLine(deadline);
  const closed = deadline.closed;
  const localStart = formatApiDateTimeLocal(ex?.start_time);
  const examGuideQs = new URLSearchParams({ name: itemTitle(ex) });
  if (courseReturnPath) examGuideQs.set("returnTo", courseReturnPath);
  const examResultQs = new URLSearchParams({ name: itemTitle(ex) });
  const examSubmitted =
    ex?.student_pdf != null && String(ex.student_pdf).trim() !== "";
  const examGraded = ex?.right_answers != null;
  const canReviewAnswers = examGraded && ex?.correction == 1;
  const examResultPath = `/exam-result/${ex.id}?${examResultQs.toString()}`;
  const examGuidePath = `/solve-exam/${ex.id}?${examGuideQs.toString()}`;
  const scoreDenom = ex?.score ?? ex?.total_count;
  const showMarkLine = examSubmitted || examGraded;
  const startQuizDisabled =
    Number(ex?.enable) === 1 || Boolean(startsIn) || closed;

  return (
    <CurriculumCard>
      <CurriculumCardMedia
        badge={examTypeLabel(ex)}
        badgeTone="exam"
        hero="linear(to-br, rgba(91, 13, 22,0.15), rgba(91, 13, 22,0.05))"
      >
        <MdAssignment color={C.secondary} size={48} />
      </CurriculumCardMedia>
      <CurriculumCardBody>
        <Text fontSize="sm" fontWeight="800" color={C.secondary} noOfLines={2} mb={1}>
          {itemTitle(ex)}
        </Text>
        <Text
          fontSize="10px"
          fontWeight="700"
          color={`${C.secondary}99`}
          textTransform="uppercase"
          letterSpacing="0.06em"
          mb={startsIn || closed || deadlineLine || showMarkLine ? 1 : 3}
        >
          {formatMetaLine(ex, "exam")}
        </Text>
        {closed ? (
          <Text
            fontSize="xs"
            fontWeight="800"
            color={C.primary}
            mb={showMarkLine ? 1 : 3}
            sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
          >
            Ended
          </Text>
        ) : (
          <>
            {startsIn ? (
              <Text
                fontSize="xs"
                fontWeight="800"
                color={C.primary}
                mb={deadlineLine || showMarkLine ? 1 : 3}
                sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
              >
                {localStart ? `${startsIn} · ${localStart}` : startsIn}
              </Text>
            ) : null}
            {deadlineLine ? (
              <Text
                fontSize="xs"
                fontWeight="800"
                color={C.primary}
                mb={showMarkLine ? 1 : 3}
                sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
              >
                {deadlineLine}
              </Text>
            ) : null}
          </>
        )}
        {showMarkLine ? (
          examGraded && scoreDenom != null && String(scoreDenom).trim() !== "" ? (
            <Text fontSize="xs" fontWeight="700" color={C.muted} mb={3}>
              Your score: {ex.right_answers} / {scoreDenom}
            </Text>
          ) : examGraded ? (
            <Text fontSize="xs" fontWeight="700" color={C.muted} mb={3}>
              Your score: {ex.right_answers}
            </Text>
          ) : (
            <Text fontSize="xs" fontWeight="700" color={C.muted} mb={3}>
              Mark: Pending
            </Text>
          )
        ) : null}
        <Box mt="auto">
          <VStack spacing={2} align="stretch">
            {examSubmitted ? (
              canReviewAnswers ? (
                <Button
                  as={Link}
                  to={examResultPath}
                  w="full"
                  size="sm"
                  bg={C.secondary}
                  color="white"
                  fontWeight="800"
                  textTransform="uppercase"
                  fontSize="10px"
                  borderRadius="lg"
                  _hover={{ bg: C.primary }}
                >
                  See answers
                </Button>
              ) : (
                <Button
                  w="full"
                  size="sm"
                  bg={C.secondary}
                  color="white"
                  fontWeight="800"
                  textTransform="uppercase"
                  fontSize="10px"
                  borderRadius="lg"
                  isDisabled
                  cursor="not-allowed"
                  _disabled={{ bg: C.secondary, opacity: 0.55 }}
                >
                  See answers
                </Button>
              )
            ) : startQuizDisabled ? (
              <Button
                w="full"
                size="sm"
                bg={C.secondary}
                color="white"
                fontWeight="800"
                textTransform="uppercase"
                fontSize="10px"
                borderRadius="lg"
                isDisabled
                cursor="not-allowed"
                _disabled={{ bg: C.secondary, opacity: 0.55 }}
              >
                {closed ? "Ended" : "Start"}
              </Button>
            ) : (
              <Button
                as={Link}
                to={examGuidePath}
                w="full"
                size="sm"
                bg={C.secondary}
                color="white"
                fontWeight="800"
                textTransform="uppercase"
                fontSize="10px"
                borderRadius="lg"
                _hover={{ bg: C.primary }}
              >
                Start
              </Button>
            )}

            {examSubmitted && !examGraded && !closed && !startsIn && Number(ex?.enable) !== 1 ? (
              <Button
                as={Link}
                to={examGuidePath}
                w="full"
                size="sm"
                variant="outline"
                borderWidth="2px"
                borderColor={C.primary}
                color={C.primary}
                fontWeight="800"
                textTransform="uppercase"
                fontSize="10px"
                borderRadius="lg"
                _hover={{ bg: "rgba(91, 13, 22, 0.06)" }}
              >
                Re-upload
              </Button>
            ) : null}

            {examSubmitted ? (
              isExamCorrected(ex) ? (
              <Button
                w="full"
                size="sm"
                variant="outline"
                borderWidth="2px"
                borderColor={C.primary}
                color={C.primary}
                fontWeight="800"
                textTransform="uppercase"
                fontSize="10px"
                borderRadius="lg"
                _hover={{ bg: "rgba(91, 13, 22, 0.06)" }}
                onClick={() => onExamPreview(ex)}
              >
                Preview PDF
              </Button>
              ) : (
              <Button
                w="full"
                size="sm"
                variant="outline"
                borderWidth="2px"
                borderColor={C.primary}
                color={C.primary}
                fontWeight="800"
                textTransform="uppercase"
                fontSize="10px"
                borderRadius="lg"
                isDisabled
                cursor="not-allowed"
                _hover={{}}
                _disabled={{ opacity: 0.55, cursor: "not-allowed" }}
              >
                Preview PDF
              </Button>
              )
            ) : null}

            {ex?.redo_flag == 1 ? (
              <Button
                w="full"
                size="sm"
                variant="outline"
                borderWidth="2px"
                borderColor={C.secondary}
                color={C.secondary}
                fontWeight="800"
                textTransform="uppercase"
                fontSize="10px"
                borderRadius="lg"
                isLoading={redoLoadingExamId === ex?.id}
                onClick={() => onRequestRedo(ex)}
                _hover={{ bg: `${C.secondary}12` }}
              >
                Request Redo
              </Button>
            ) : null}
          </VStack>
        </Box>
      </CurriculumCardBody>
    </CurriculumCard>
  );
}

function LiveCurriculumCard({ lv }) {
  const now = useCountdownNow(true, 1000);
  const phase = getLiveSessionPhase(lv?.start_time, now);
  const liveNow = lv.is_live === 1 || lv.is_live === true || lv.status === "live";
  const showLiveBadge = liveNow || phase === "open";

  return (
    <CurriculumCard>
      <CurriculumCardMedia
        badge="Live session"
        hero="linear(to-br, rgba(91, 13, 22,0.2), rgba(91, 13, 22,0.1))"
        topRight={
          showLiveBadge ? (
            <Box
              bg="red.600"
              color="white"
              fontSize="9px"
              fontWeight="800"
              textTransform="uppercase"
              letterSpacing="0.06em"
              px={2}
              py={0.5}
              borderRadius="full"
            >
              Live now
            </Box>
          ) : phase === "ended" ? (
            <Box
              bg="gray.500"
              color="white"
              fontSize="9px"
              fontWeight="800"
              textTransform="uppercase"
              letterSpacing="0.06em"
              px={2}
              py={0.5}
              borderRadius="full"
            >
              Ended
            </Box>
          ) : null
        }
      >
        <MdCalendarMonth color={C.primary} size={48} />
      </CurriculumCardMedia>
      <CurriculumCardBody>
        <Text fontSize="sm" fontWeight="800" color={C.primary} noOfLines={2} mb={1}>
          {itemTitle(lv)}
        </Text>
        <Text
          fontSize="10px"
          fontWeight="700"
          color={`${C.primary}99`}
          textTransform="uppercase"
          letterSpacing="0.06em"
          mb={3}
        >
          {formatMetaLine(lv, "live")}
        </Text>
        <Box mt="auto">
          <LiveSessionJoinControls lv={lv} fullWidth />
        </Box>
      </CurriculumCardBody>
    </CurriculumCard>
  );
}

/** One shell per card so hero + body share a single border (no seam between stacked boxes). */
const CurriculumCard = ({ children }) => (
  <Box
    bg={studentSurfaces.raised}
    borderWidth="1px"
    borderColor={C.border}
    borderRadius="xl"
    overflow="hidden"
    transition="transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease"
    willChange="transform"
    _hover={{
      borderColor: `${C.primary}66`,
      transform: "scale(1.02)",
      boxShadow:
        "0 18px 46px rgba(91, 13, 22, 0.18), 0 0 0 1px rgba(168, 160, 150, 0.35)",
    }}
    _active={{ transform: "scale(1.01)" }}
    display="flex"
    flexDirection="column"
    h="100%"
  >
    {children}
  </Box>
);

const CurriculumCardMedia = ({ children, badge, badgeTone = "primary", topRight, hero }) => (
  <Box
    position="relative"
    h={{ base: "132px", sm: "144px" }}
    overflow="hidden"
    bgGradient={hero || "linear(to-br, rgba(91, 13, 22,0.12), rgba(91, 13, 22,0.06))"}
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <Box
      position="absolute"
      top={3}
      left={3}
      zIndex={2}
      bg={
        badgeTone === "exam"
          ? C.secondary
          : badgeTone === "homework"
          ? C.tertiary
          : badgeTone === "pdf"
          ? C.tertiary
          : C.primary
      }
      color="white"
      fontSize="9px"
      fontWeight="800"
      textTransform="uppercase"
      letterSpacing="0.06em"
      px={2}
      py={0.5}
      borderRadius="full"
    >
      {badge}
    </Box>
    {topRight ? (
      <Box position="absolute" top={3} right={3}>
        {topRight}
      </Box>
    ) : null}
    {children}
  </Box>
);

const CurriculumCardBody = ({ children }) => (
  <Box
    p={4}
    borderTopWidth="1px"
    borderTopColor="rgba(216, 206, 195, 0.75)"
    display="flex"
    flexDirection="column"
    flex="1"
    minH="168px"
  >
    {children}
  </Box>
);

const ChapterContentGrid = ({
  bucket,
  chapter,
  onWatchVideo,
  courseReturnPath,
  onOpenPdf,
  contentFilter = "all",
  emptyMessage,
  onContentRefresh,
}) => {
  const dispatch = useDispatch();
  const voucherLoading = useSelector((s) => s.chapterVideos.voucherLoading);
  const [homeworkForModal, setHomeworkForModal] = useState(null);
  const [redoLoadingExamId, setRedoLoadingExamId] = useState(null);

  if (!bucket || bucket.loading) {
    return (
      <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4} p={5}>
        {[1, 2, 3].map((k) => (
          <Skeleton key={k} h="200px" borderRadius="xl" />
        ))}
      </Grid>
    );
  }

  if (bucket.error) {
    return (
      <Text p={5} color="red.600" fontWeight="600" fontSize="sm">
        {bucket.error}
      </Text>
    );
  }

  const allExams = bucket.exams || [];
  const allHomeworks = bucket.homeworks || [];

  let videos = [];
  let lessons = [];
  let liveVideos = [];
  let exams = [];
  let homeworks = [];

  if (contentFilter === "homework") {
    homeworks = allHomeworks;
    exams = allExams.filter((ex) => Number(ex?.type) === 2);
  } else if (contentFilter === "quizzes") {
    exams = allExams.filter((ex) => Number(ex?.type) === 0);
  } else if (contentFilter === "exams") {
    exams = allExams.filter((ex) => {
      const t = Number(ex?.type);
      return t === 1 || t === 6;
    });
  } else {
    videos = bucket.videos || [];
    lessons = bucket.lessons || [];
  }

  const hasAny =
    videos.length +
      lessons.length +
      exams.length +
      liveVideos.length +
      homeworks.length >
    0;

  if (!hasAny) {
    return (
      <Text p={5} color={C.muted} fontWeight="600" fontSize="sm">
        {emptyMessage ||
          (contentFilter === "homework"
            ? "No homework in this lesson yet."
            : contentFilter === "quizzes"
              ? "No quizzes in this lesson yet."
              : contentFilter === "exams"
              ? "No exams in this lesson yet."
              : "No videos or PDFs in this lesson yet.")}
      </Text>
    );
  }

  const chapterLabel = (chapter?.name || "Lesson").toUpperCase();

  const handleWatch = (video) => {
    if (video?.suspended != 1) {
      countSeens(dispatch, video);
    }
    onWatchVideo?.(video, chapterLabel);
  };

  const handleHomeworkPrimary = (hw) => {
    const sh = hw?.student_homework;
    const hasFile = sh?.file;
    const status = sh?.status;
    if ((status === 0 || status === 1) && hasFile) {
      if (!isHomeworkCorrected(hw)) return;
      const url = homeworkSubmissionPdfUrl(hasFile);
      if (url)
        onOpenPdf?.({
          url,
          title: itemTitle(hw),
          backLabel: "Back to curriculum",
        });
    } else if (status === 2 || !sh) {
      setHomeworkForModal(hw);
      dispatch(openSolveHomeworkModel());
    }
  };

  const handleHomeworkPreview = (hw) => {
    if (!isHomeworkCorrected(hw)) return;
    const hasFile = hw?.student_homework?.file;
    if (!hasFile) return;
    const url = homeworkSubmissionPdfUrl(hasFile);
    if (url) {
      onOpenPdf?.({
        url,
        title: itemTitle(hw),
        backLabel: "Back to curriculum",
      });
    }
  };

  const handleExamPreview = (ex) => {
    if (!isExamCorrected(ex)) return;
    const hasFile = ex?.student_pdf;
    if (!hasFile || String(hasFile).trim() === "") return;
    const url = examSubmissionPdfUrl(hasFile);
    if (url) {
      onOpenPdf?.({
        url,
        title: itemTitle(ex),
        backLabel: "Back to curriculum",
      });
    }
  };

  const homeworkDisabled = (hw) => hw?.active === 0 || hw?.enable === 0;

  return (
    <Box p={5}>
      <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
        {videos.map((v) => {
          const alert = videoAlertMessage(v);
          const alertColor =
            v?.views_count?.views_count >= 4 ? "#b83232" : `${C.primary}cc`;
          const showVoucherRow = needsVoucherActions(v);
          const showWatchButton = !alert && !showVoucherRow;
          const title = itemTitle(v);
          const videoThumbUrl = videoThumbnailUrl(v?.thumbnail);
          const questionCode = questionCodeFromTitle(title);

          return (
            <CurriculumCard key={`v-${v.id}`}>
              <CurriculumCardMedia
                badge="Session"
                badgeTone="primary"
                hero={
                  videoThumbUrl
                    ? "linear(to-br, rgba(0,0,0,0.0), rgba(0,0,0,0.0))"
                    : questionCode
                    ? "linear(to-br, #FBF6F1, #FBF6F1)"
                    : "linear(to-br, rgba(91, 13, 22,0.15), rgba(91, 13, 22,0.05))"
                }
              >
                {videoThumbUrl ? (
                  <>
                    <Image
                      src={videoThumbUrl}
                      alt={`${title} thumbnail`}
                      position="absolute"
                      inset={0}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      objectPosition="center"
                      display="block"
                    />
                    <Box
                      position="absolute"
                      inset={0}
                      bgGradient="linear(to-br, rgba(91, 13, 22,0.25), rgba(91, 13, 22,0.14))"
                    />
                    <Box position="relative" zIndex={1}>
                      <MdPlayCircle color="white" size={52} />
                    </Box>
                  </>
                ) : questionCode ? (
                  <QuestionCodePlaceholder label={questionCode} />
                ) : (
                  <MdPlayCircle color={C.primary} size={48} />
                )}
              </CurriculumCardMedia>
              <CurriculumCardBody>
                <Text fontSize="sm" fontWeight="800" color={C.primary} noOfLines={2} mb={1}>
                  {title}
                </Text>
                <Text fontSize="10px" fontWeight="700" color={`${C.primary}99`} textTransform="uppercase" letterSpacing="0.06em" mb={2}>
                  {formatMetaLine(v, "video")}
                </Text>
                {alert ? (
                  <Text fontSize="xs" fontWeight="600" color={alertColor} mb={3} lineHeight="1.45">
                    {alert}
                  </Text>
                ) : null}
                <Box mt="auto">
                  {showVoucherRow ? (
                    <Flex direction={{ base: "column", sm: "row" }} gap={2} mb={showWatchButton ? 2 : 0}>
                      <Button
                        flex={1}
                        size="sm"
                        bg={C.primary}
                        color="white"
                        fontWeight="800"
                        textTransform="uppercase"
                        fontSize="10px"
                        borderRadius="lg"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const fd = new FormData();
                          fd.append("video_id", v.id);
                          dispatch(moreVideoViewsRequest(fd));
                        }}
                      >
                        Request voucher
                      </Button>
                      <Button
                        flex={1}
                        size="sm"
                        variant="outline"
                        borderWidth="2px"
                        borderColor={C.primary}
                        color={C.primary}
                        fontWeight="800"
                        textTransform="uppercase"
                        fontSize="10px"
                        borderRadius="lg"
                        isLoading={voucherLoading}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          dispatch(openAddVoucherModel(v.id));
                        }}
                      >
                        Add voucher
                      </Button>
                    </Flex>
                  ) : null}
                  {showWatchButton ? (
                    <Button
                      w="full"
                      size="sm"
                      variant="outline"
                      borderWidth="2px"
                      borderColor={C.primary}
                      color={C.primary}
                      fontWeight="800"
                      textTransform="uppercase"
                      fontSize="10px"
                      borderRadius="lg"
                      sx={studentOutlineButtonDarkSx}
                      _hover={{ bg: `${C.primary}12` }}
                      onClick={() => handleWatch(v)}
                    >
                      Watch
                    </Button>
                  ) : null}
                </Box>
              </CurriculumCardBody>
            </CurriculumCard>
          );
        })}

        {lessons.map((pdf) => {
          const pdfUrl = pdf?.file ? lessonPdfPublicUrl(pdf.file) : "";
          return (
            <CurriculumCard key={`l-${pdf.id}`}>
              <CurriculumCardMedia
                badge="PDF"
                badgeTone="pdf"
                hero="linear(to-br, rgba(214,206,192,0.65), rgba(247,242,236,0.95))"
              >
                <MdDescription color={C.primary} size={48} />
              </CurriculumCardMedia>
              <CurriculumCardBody>
                <Text fontSize="sm" fontWeight="800" color={C.primary} noOfLines={2} mb={1}>
                  {itemTitle(pdf)}
                </Text>
                <Text fontSize="10px" fontWeight="700" color={`${C.primary}99`} textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                  {formatMetaLine(pdf, "lesson")}
                </Text>
                <Box mt="auto">
                  <Button
                    w="full"
                    size="sm"
                    bg={C.primary}
                    color="white"
                    fontWeight="800"
                    textTransform="uppercase"
                    fontSize="10px"
                    borderRadius="lg"
                    isDisabled={!pdfUrl}
                    _hover={{ bg: C.secondary }}
                    onClick={() => {
                      if (!pdfUrl) return;
                      onOpenPdf?.({
                        url: pdfUrl,
                        title: itemTitle(pdf),
                        backLabel: "Back to curriculum",
                      });
                    }}
                  >
                    View PDF
                  </Button>
                </Box>
              </CurriculumCardBody>
            </CurriculumCard>
          );
        })}

        {exams.map((ex) => (
          <ExamCurriculumCard
            key={`e-${ex.id}`}
            ex={ex}
            courseReturnPath={courseReturnPath}
            redoLoadingExamId={redoLoadingExamId}
            onExamPreview={handleExamPreview}
            onRequestRedo={(exam) => {
              setRedoLoadingExamId(exam?.id);
              const formData = new FormData();
              formData.append("exam_id", exam?.id);
              const action = () => setRedoLoadingExamId(null);
              dispatch(examRedoRequest(formData, action));
            }}
          />
        ))}

        {liveVideos.map((lv) => (
          <LiveCurriculumCard key={`lv-${lv.id}`} lv={lv} />
        ))}

        {homeworks.map((hw) => (
          <HomeworkCurriculumCard
            key={`hw-${hw.id}`}
            hw={hw}
            disabled={homeworkDisabled(hw)}
            onPrimary={() => handleHomeworkPrimary(hw)}
            onPreview={() => handleHomeworkPreview(hw)}
          />
        ))}
      </Grid>
      <SolveHomework
        data={homeworkForModal}
        setSelectedData={setHomeworkForModal}
        onSubmitted={() => {
          if (chapter?.id) dispatch(getChapterContentRequest(chapter.id));
          onContentRefresh?.();
        }}
      />
    </Box>
  );
};

const getHeroStyles = (isDark) => ({
  bg: studentSurfaces.canvas,
  text: isDark ? C.cream : C.secondary,
  textMuted: isDark ? "rgba(247, 242, 236, 0.65)" : C.secondary,
  textSubtle: isDark ? "rgba(247, 242, 236, 0.78)" : C.secondary,
  border: isDark ? "rgba(247, 242, 236, 0.12)" : C.stone,
  imageBorder: isDark ? "rgba(247, 242, 236, 0.12)" : "rgba(91, 13, 22, 0.15)",
  placeholderBg: isDark ? "rgba(247, 242, 236, 0.08)" : "rgba(91, 13, 22, 0.06)",
});

const StatTile = ({ icon: Icon, label, value, borderRight = false, borderBottom = false, hero }) => (
  <Flex
    bg={hero.bg}
    p={4}
    align="center"
    gap={4}
    borderRightWidth={borderRight ? "1px" : 0}
    borderBottomWidth={borderBottom ? "1px" : 0}
    borderColor={hero.border}
    minH="72px"
  >
    <Box color={hero.textSubtle} flexShrink={0}>
      <Icon size={22} />
    </Box>
    <Box minW={0}>
      <Text fontWeight="800" color={hero.text} fontSize="lg" lineHeight={1}>
        {value}
      </Text>
      <Text
        fontSize="10px"
        fontWeight="800"
        color={hero.textMuted}
        textTransform="uppercase"
        letterSpacing="0.08em"
      >
        {label}
      </Text>
    </Box>
  </Flex>
);

const CourseStatsGrid = ({
  sessionsCount,
  liveCount,
  pdfsCount,
  examsCount,
  homeworksCount,
  pendingExamsCount,
  pendingHomeworksCount,
  hero,
  variant = "full",
}) => (
  <Box
    borderWidth="1px"
    borderColor={hero.border}
    borderRadius="xl"
    overflow="hidden"
    mb={6}
  >
    {variant === "course" ? (
      <Grid templateColumns="repeat(2, 1fr)">
        <StatTile icon={MdMenuBook} label="Sessions" value={sessionsCount} borderRight hero={hero} />
        <StatTile icon={MdDescription} label="PDFs" value={pdfsCount} hero={hero} />
      </Grid>
    ) : (
      <>
        <Grid templateColumns="repeat(3, 1fr)">
          <StatTile icon={MdMenuBook} label="Sessions" value={sessionsCount} borderRight borderBottom hero={hero} />
          <StatTile icon={MdVideoLibrary} label="Live sessions" value={liveCount} borderRight borderBottom hero={hero} />
          <StatTile icon={MdDescription} label="PDFs" value={pdfsCount} borderBottom hero={hero} />
        </Grid>
        <Grid templateColumns="repeat(2, 1fr)">
          <StatTile icon={MdQuiz} label="Exams" value={examsCount} borderRight borderBottom hero={hero} />
          <StatTile icon={MdHomeWork} label="Homework" value={homeworksCount} borderBottom hero={hero} />
        </Grid>
        <Grid templateColumns="repeat(2, 1fr)">
          <StatTile icon={MdPendingActions} label="Exams left" value={pendingExamsCount} borderRight hero={hero} />
          <StatTile icon={MdPendingActions} label="Homework left" value={pendingHomeworksCount} hero={hero} />
        </Grid>
      </>
    )}
  </Box>
);

const StudentCourseDetails = () => {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { themeMode } = useAdminTheme();
  const hero = useMemo(() => getHeroStyles(themeMode === "dark"), [themeMode]);
  const sections = useSelector((s) => s.sections.sections);
  const sectionChapters = useSelector((s) => s.sections.section);
  const sectionsLoading = useSelector((s) => s.sections.isLoading);
  const chapterContentById = useSelector((s) => s.studentChapters.chapterContentById);

  const [openIdx, setOpenIdx] = useState([]);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [modalChapterLabel, setModalChapterLabel] = useState("");
  const [pdfPreview, setPdfPreview] = useState(null);

  const openVideoModal = (video, chapterLabel) => {
    setActiveVideo(video ?? null);
    setActiveVideoId(video?.id ?? null);
    setModalChapterLabel(chapterLabel || "");
    setVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setVideoModalOpen(false);
    setActiveVideoId(null);
    setActiveVideo(null);
    setModalChapterLabel("");
  };

  const meta = useMemo(() => {
    const raw = sections;
    const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    return list.find((x) => String(x.id) === String(courseId)) || null;
  }, [sections, courseId]);

  const chapters = useMemo(() => {
    const ch = sectionChapters;
    return Array.isArray(ch) ? ch : Array.isArray(ch?.data) ? ch.data : [];
  }, [sectionChapters]);

  useEffect(() => {
    setOpenIdx([]);
  }, [courseId]);

  useEffect(() => {
    dispatch(getAllSectionsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (courseId) dispatch(getSectionRequest(courseId));
  }, [dispatch, courseId]);

  const ensureContent = useCallback(
    (indices) => {
      indices.forEach((i) => {
        const ch = chapters[i];
        if (!ch?.id) return;
        const bucket = chapterContentById[ch.id];
        if (!bucket?.loaded && !bucket?.loading) {
          dispatch(getChapterContentRequest(ch.id));
        }
      });
    },
    [chapters, chapterContentById, dispatch]
  );

  useEffect(() => {
    const chapterParam = searchParams.get("chapter");
    if (!chapterParam || chapters.length === 0) return;
    const idx = chapters.findIndex((ch) => String(ch.id) === String(chapterParam));
    if (idx < 0) return;
    setOpenIdx((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
    ensureContent([idx]);
  }, [searchParams, chapters, ensureContent]);

  const handleAccordionChange = (expandedIndex) => {
    const next = Array.isArray(expandedIndex)
      ? expandedIndex
      : expandedIndex === -1
        ? []
        : [expandedIndex];
    setOpenIdx(next);
    ensureContent(next);
  };

  const expandAll = () => {
    const all = chapters.map((_, i) => i);
    setOpenIdx(all);
    ensureContent(all);
  };

  const img = publicAssetUrl(meta?.image);
  const progress = meta?.progress ?? meta?.completion_percent ?? 0;
  const lessonsCount = meta?.lessons_count ?? chapters.length;
  const videosCount = meta?.videos_count ?? 0;
  const pdfsCount = meta?.pdfs_count ?? 0;

  return (
    <StudentPortalShell>
      <Flex direction="column" minH={{ md: "100vh" }}>
        <Box bg={hero.bg} w="100%" py={{ base: 10, md: 16 }} px={{ base: 4, md: 12 }} borderBottomWidth="1px" borderColor={hero.border}>
          <Flex
            direction={{ base: "column", md: "row" }}
            align={{ base: "stretch", md: "center" }}
            maxW="5xl"
            w="100%"
            gap={{ base: 8, md: 12 }}
          >
            <Box w={{ base: "100%", md: "42%" }} flexShrink={0}>
              {img ? (
                <Image
                  src={img}
                  alt={meta?.name || "Course"}
                  w="100%"
                  aspectRatio={1}
                  objectFit="cover"
                  borderWidth="4px"
                  borderColor={hero.imageBorder}
                  borderRadius="xl"
                  boxShadow={`8px 8px 0 0 ${C.secondary}`}
                />
              ) : (
                <Flex
                  aspectRatio={1}
                  borderRadius="xl"
                  bg={hero.placeholderBg}
                  align="center"
                  justify="center"
                  color={hero.text}
                  fontWeight="900"
                  fontSize="4xl"
                  borderWidth="4px"
                  borderColor={hero.imageBorder}
                >
                  {(meta?.name || "?").slice(0, 2).toUpperCase()}
                </Flex>
              )}
            </Box>
            <Box flex={1} color={hero.text}>
              <Text
                fontSize="xs"
                fontWeight="800"
                letterSpacing="0.2em"
                textTransform="uppercase"
                color={hero.textMuted}
                mb={2}
              >
                Course overview
              </Text>
              <Heading
                as="h1"
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="800"
                color={hero.text}
                textTransform="uppercase"
                letterSpacing="tight"
                lineHeight="1.15"
                mb={6}
              >
                {meta?.name || "Course"}
              </Heading>

              <CourseStatsGrid
                variant="course"
                sessionsCount={meta?.sessions_count ?? videosCount ?? lessonsCount}
                pdfsCount={pdfsCount}
                hero={hero}
              />

            </Box>
          </Flex>
        </Box>

        <Box maxW="4xl" mx="auto" py={{ base: 10, md: 16 }} px={{ base: 4, md: 6 }} w="100%">
          <Flex align="center" justify="space-between" borderBottomWidth="2px" borderColor={C.primary} pb={4} mb={10} flexWrap="wrap" gap={4}>
            <Heading fontSize="2xl" fontWeight="800" color={C.primary} textTransform="uppercase" letterSpacing="tight">
              Curriculum content
            </Heading>
          </Flex>

          {sectionsLoading && chapters.length === 0 ? (
            <VStack spacing={4} align="stretch">
              {[1, 2, 3].map((k) => (
                <Skeleton key={k} h="72px" borderRadius="xl" />
              ))}
            </VStack>
          ) : chapters.length === 0 ? (
            <Text color={C.muted} fontWeight="600">
              No lessons are published for this course yet.
            </Text>
          ) : (
            <Accordion allowMultiple index={openIdx} onChange={handleAccordionChange}>
              {chapters.map((chapter, index) => (
                <AccordionItem
                  key={chapter.id}
                  bg={studentSurfaces.raised}
                  borderWidth="1px"
                  borderColor={C.border}
                  borderRadius="xl"
                  mb={4}
                  overflow="hidden"
                >
                  <AccordionButton
                    px={6}
                    py={5}
                    _hover={{ bg: studentSurfaces.muted }}
                    borderBottomWidth="1px"
                    borderColor={C.border}
                  >
                    <Flex flex={1} align="center" gap={4} textAlign="left">
                      <Text fontWeight="800" fontSize="2xl" color={C.primary} opacity={0.2} minW="36px">
                        {String(index + 1).padStart(2, "0")}
                      </Text>
                      <Text flex={1} fontWeight="700" fontSize="md" color={C.primary}>
                        {chapter.name || `Lesson ${index + 1}`}
                      </Text>
                      {chapterContentById[chapter.id]?.loading ? <Spinner size="sm" color={C.primary} /> : null}
                    </Flex>
                    <AccordionIcon color={C.primary} />
                  </AccordionButton>
                  <AccordionPanel p={0}>
                    <ChapterContentGrid
                      bucket={chapterContentById[chapter.id]}
                      chapter={chapter}
                      onWatchVideo={openVideoModal}
                      courseReturnPath={courseId ? `/students/courses/${courseId}` : ""}
                      onOpenPdf={setPdfPreview}
                    />
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          )}

          <Flex
            mt={16}
            pt={8}
            borderTopWidth="1px"
            borderColor={C.border}
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            gap={8}
            align={{ base: "flex-start", md: "center" }}
          >
            <Flex gap={{ base: 6, md: 12 }} flexWrap="wrap">
              {meta?.created_at || meta?.enrolled_at ? (
                <Box>
                  <Text fontSize="10px" fontWeight="800" color={`${C.primary}66`} textTransform="uppercase" letterSpacing="0.1em" mb={1}>
                    Enrolled
                  </Text>
                  <Text fontWeight="700" color={C.primary} fontSize="sm">
                    {meta.enrolled_at || meta.created_at}
                  </Text>
                </Box>
              ) : null}
              {meta?.expected_end || meta?.ends_at ? (
                <Box>
                  <Text fontSize="10px" fontWeight="800" color={`${C.primary}66`} textTransform="uppercase" letterSpacing="0.1em" mb={1}>
                    Expected end
                  </Text>
                  <Text fontWeight="700" color={C.primary} fontSize="sm">
                    {meta.expected_end || meta.ends_at}
                  </Text>
                </Box>
              ) : null}
              {meta?.teacher_name || meta?.instructor_name ? (
                <Box>
                  <Text fontSize="10px" fontWeight="800" color={`${C.primary}66`} textTransform="uppercase" letterSpacing="0.1em" mb={1}>
                    Instructor
                  </Text>
                  <Text fontWeight="700" color={C.primary} fontSize="sm" textDecor="underline" textUnderlineOffset={4}>
                    {meta.teacher_name || meta.instructor_name}
                  </Text>
                </Box>
              ) : null}
            </Flex>
            <Button
              as={ChakraLink}
              href={meta?.syllabus_url || meta?.syllabus_pdf}
              isDisabled={!(meta?.syllabus_url || meta?.syllabus_pdf)}
              bg={C.primary}
              color="white"
              fontWeight="800"
              fontSize="xs"
              textTransform="uppercase"
              letterSpacing="0.12em"
              px={8}
              py={6}
              borderRadius="xl"
              _hover={{ bg: C.secondary, textDecoration: "none" }}
            >
              Course syllabus PDF
            </Button>
          </Flex>
        </Box>
      </Flex>
      <CourseVideoModal
        isOpen={Boolean(videoModalOpen && activeVideoId)}
        onClose={closeVideoModal}
        chapterLabel={modalChapterLabel}
        videoId={activeVideoId}
        initialVideoFiles={activeVideo?.files}
        videoTitleFallback={activeVideo?.title}
        onOpenPdf={setPdfPreview}
      />
      <PdfPreviewModal
        isOpen={Boolean(pdfPreview?.url)}
        onClose={() => setPdfPreview(null)}
        pdfUrl={pdfPreview?.url || ""}
        title={pdfPreview?.title}
        backLabel={pdfPreview?.backLabel}
        totalPages={pdfPreview?.totalPages}
      />
      <AddVoucher />
    </StudentPortalShell>
  );
};

export default StudentCourseDetails;

export { ChapterContentGrid, CourseStatsGrid, getHeroStyles };
